import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import TableSimple from 'src/components/TableSimple';
import { CustomRowRenderParamsType, CustomRowRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import translate from '@translate';
import { colors, sizes } from 'src/themes/default';
import { OrderProductSetWithProductType, OrderProductType, OrderType } from 'src/types/order';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { productsDefaultColumns } from './constants';
import sumArray, { sumArraySimple } from 'src/utils/sumArray';
import { formatNumber } from 'src/utils/formatNumber';
import { styled } from '@mui/system';
import { getRowElementText } from 'src/components/Table/functions';
import Box from '@mui/material/Box';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import TableRowActions from 'src/components/Table/TableRowActions';
import Checkbox from 'src/components/Checkbox';
import { ProductCustomizationIcon } from 'src/components/SkladonIcon/SmallIcons';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import ProductNameLink from 'src/components/ProductNameLink';
import {
    changeOrderProductCount,
    changeOrderSetCount,
    deactivateOrderSet,
    finalizeOrderProductCountOnBlur,
    finalizeOrderSetCountOnBlur,
    getProductSetData,
    getVisibleSetProducts,
    hasActiveFilters,
    isSetRowVisibleForFilters,
    ProductCountInputValue,
    toProductCountInputValue,
} from './functions';
import { shouldApplyProductQuantityInput } from 'src/utils/productQuantityInput';
import { getOrderProductDisplayName, getOrderProductDisplaySku } from 'src/utils/orderProductDisplay';
import {
    computeOrderProductTotalPrice,
    flattenProductCatalogFields,
} from 'src/utils/productCatalogTableColumns';
import { FormErrorsType } from '../../types/global';
import { FilteringRowType, GridFilterType, TablePropsOptionsType } from 'src/types/table';
import { filteringRow } from 'src/components/TableSimple/functions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import colorOptionFce from 'src/utils/colorOptionFce';

const isReactivationBlocked = (row: TableRowType): boolean => {
    if (Number(row.REACTIVATION_BLOCKED) === 1) {
        return true;
    }
    if (row.products?.some((product: TableRowType) => Number(product.REACTIVATION_BLOCKED) === 1)) {
        return true;
    }
    return false;
};

const SetRowStartVertical = styled('div')<{
    last?: boolean;
}>(({ last }) => ({
    position: 'absolute',
    top: '0px',
    bottom: last ? '22px' : '0px',
    width: '2px',
    backgroundColor: colors.text_T25
}));
const SetRowStartHorizontal = styled('div')({
    left: '10px',
    top: '45px',
    bottom: '18px',
    backgroundColor: colors.text_T25,
    height: '2px',
    marginBottom: '4px',
    width: '7px',
    display: 'inline-block',
    marginRight: '5px'
});

const arrowStyle = { marginLeft: '-9px' };

export interface ProductsTablePropsType {
    data?: OrderProductType[];
    newOrder: boolean;
    allowOrderProductDetail: boolean;
    onChange: (value: OrderProductType[], error?: boolean, errorKey?: string) => void;
    onChangeProductsForSets: (value: OrderProductSetWithProductType[]) => void;
    disableEdit?: boolean;
    orderProductCustomsTypeCodes: ConstantsCodeType[];
    orderProductCustomizationTypeCodes: ConstantsCodeType[];
    summary?: boolean;
    productsForSets: OrderProductSetWithProductType[];
    orderData: OrderType | null;
    setOpenProductDetailId: (value: number) => void;
    editErrors: FormErrorsType;
}

interface OrderProducSetType extends Omit<OrderProductType, 'ORDERS_PRODUCTS_ID'> {
    products?: OrderProducSetType[];
    ORDERS_PRODUCTS_ID?: string | number;
    ID_SET?: number;
    COUNTProducts?: number;
    MISSING_COUNTProducts?: number;
    CONFIRMEDProducts?: number;
    TOTAL_PRICE?: number | null;
    PRODUCT_TYPE?: string | null;
}

interface RenderSetRowParamsType extends CustomRowRenderParamsType {
    onClick?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
    last?: boolean;
}

type RenderSetRowType = (params: RenderSetRowParamsType) => ReactNode | undefined;

const flattenOrderProductRow = (item: OrderProductType): OrderProducSetType => {
    const catalog = flattenProductCatalogFields(item.PRODUCT);
    const productPrice = item.PRODUCT_PRICE;
    const count = item.COUNT;
    return {
        ...item,
        ...catalog,
        // Prefer line PRODUCT_ID; fall back to catalog PRODUCT_ID from nested PRODUCT
        PRODUCT_ID: item.PRODUCT_ID ?? catalog.PRODUCT_ID,
        NAME: getOrderProductDisplayName(item),
        SKU: getOrderProductDisplaySku(item),
        PRODUCT_TYPE: catalog.PRODUCT_TYPE ?? item.PRODUCT?.PRODUCT_TYPE ?? null,
        ORDERS_PRODUCTS_ID: String(item.ORDERS_PRODUCTS_ID),
        TOTAL_PRICE: item.ID_SET
            ? null
            : computeOrderProductTotalPrice(productPrice, count),
    };
};

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const dispatch = useDispatch();
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);

    const [data, setData] = useState<OrderProducSetType[]>([]);
    const [allColumns, setAllColumns] = useState<TableColumnType[]>(productsDefaultColumns);
    const [openSet, setOpenSet] = useState<{ [key: string]: boolean }>({});
    const [activeFilters, setActiveFilters] = useState<GridFilterType[]>([]);
    const [extendProductTypeCodes, setExtendProductTypeCodes] = useState<ConstantsCodeType[]>([]);

    const getInputName = (id: number | string, key: string) => `product-${id}-${key}`;
    const countOnFocusRef = useRef<Record<string, number>>({});
    const lastInputValueRef = useRef<Record<string, ProductCountInputValue>>({});

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const handleCountFocus = (name: string, currentCount: number) => {
        countOnFocusRef.current[name] = currentCount;
        lastInputValueRef.current[name] = currentCount;
    };

    const handleProductCountChange = (
        ordersProductsId: number | string,
        value: string | number | undefined | null,
        error: boolean,
        errorKey: string
    ) => {
        const inputValue = toProductCountInputValue(value);
        lastInputValueRef.current[errorKey] = inputValue;
        if (!shouldApplyProductQuantityInput(inputValue)) {
            return;
        }
        const products = changeOrderProductCount(props.data || [], ordersProductsId, inputValue);
        props.onChange(products, error, errorKey);
    };

    const handleProductCountBlur = (ordersProductsId: number | string, errorKey: string) => {
        const lastValue = lastInputValueRef.current[errorKey] ?? countOnFocusRef.current[errorKey] ?? 'empty';
        const countOnFocus = countOnFocusRef.current[errorKey] ?? 0;
        const products = finalizeOrderProductCountOnBlur(
            props.data || [],
            ordersProductsId,
            lastValue,
            countOnFocus
        );
        props.onChange(products, false, errorKey);
    };

    const handleSetCountChange = (
        idSet: number,
        value: string | number | undefined | null,
        error: boolean,
        errorKey: string
    ) => {
        const inputValue = toProductCountInputValue(value);
        lastInputValueRef.current[errorKey] = inputValue;
        if (!shouldApplyProductQuantityInput(inputValue)) {
            return;
        }
        const { products, productsForSets } = changeOrderSetCount(
            props.data || [],
            props.productsForSets,
            idSet,
            inputValue
        );
        props.onChange(products, error, errorKey);
        props.onChangeProductsForSets(productsForSets);
    };

    const handleSetCountBlur = (idSet: number, errorKey: string) => {
        const lastValue = lastInputValueRef.current[errorKey] ?? countOnFocusRef.current[errorKey] ?? 'empty';
        const setCountOnFocus = countOnFocusRef.current[errorKey] ?? 0;
        const { products, productsForSets } = finalizeOrderSetCountOnBlur(
            props.data || [],
            props.productsForSets,
            idSet,
            lastValue,
            setCountOnFocus
        );
        props.onChange(products, false, errorKey);
        props.onChangeProductsForSets(productsForSets);
    };

    const customRender = (column: TableColumnType, row: TableRowType): ReactNode | string | undefined => {
        const disabledRow = row.LINE_STATE === 'D';
        const isSetChild = !!row.ID_SET && !row.products;
        const isSetParent = !!row.products;

        switch (column.key) {
            case 'COUNT': {
                if (isSetChild) {
                    return formatNumber(row.COUNT ?? '');
                }
                const name = getInputName(row.ORDERS_PRODUCTS_ID, column.key);
                const disabled =
                    !!props.disableEdit || !!props.summary || disabledRow;
                if (isSetParent && row.ID_SET) {
                    return (
                        <TextFieldDebounced
                            value={row.COUNT}
                            error={props.editErrors[name]}
                            onChange={(value, inputError) =>
                                handleSetCountChange(row.ID_SET, value, inputError, name)
                            }
                            onFocus={() => handleCountFocus(name, Number(row.COUNT || 0))}
                            onBlur={() => handleSetCountBlur(row.ID_SET, name)}
                            name={name}
                            fullWidth
                            type="numberLocale"
                            min={1}
                            step="1"
                            required={!disabled}
                            disabled={disabled}
                        />
                    );
                }
                return (
                    <TextFieldDebounced
                        value={row.COUNT}
                        error={props.editErrors[name]}
                        onChange={(value, inputError) =>
                            handleProductCountChange(row.ORDERS_PRODUCTS_ID, value, inputError, name)
                        }
                        onFocus={() => handleCountFocus(name, Number(row.COUNT || 0))}
                        onBlur={() => handleProductCountBlur(row.ORDERS_PRODUCTS_ID, name)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={1}
                        step="1"
                        required={!disabled}
                        disabled={disabled}
                    />
                );
            }
            case 'MISSING_COUNT':
                if (row.LINE_STATE !== 'D' && row.MISSING_COUNT && row.MISSING_COUNT > 0) {
                    return <span style={{ color: colors.alert_O }}>{formatNumber(row.MISSING_COUNT)}</span>;
                }
                break;
            case 'PRODUCT_PRICE':
                if (isSetChild) {
                    return <span></span>;
                }
                return formatNumber(row.PRODUCT_PRICE ?? '');
            case 'TOTAL_PRICE':
                if (isSetChild) {
                    return <span></span>;
                }
                return formatNumber(row.TOTAL_PRICE ?? '');
            case 'NAME':
                if (isSetParent) {
                    return row.NAME;
                }
                return (
                    <ProductNameLink
                        productId={row.PRODUCT_ID ?? row.PRODUCT?.ID}
                        name={row.NAME}
                    />
                );
        }
        return undefined;
    };

    useEffect(() => {
        if (productTypeCodes) {
            setExtendProductTypeCodes([
                ...productTypeCodes,
                {
                    label: translate('utils.orderProductCustomsTypeCodes.productSet'),
                    rawLabel: 'productSet',
                    value: 'productSet',
                    color: colors.primary_O,
                },
            ]);
        }
    }, [productTypeCodes]);

    useEffect(() => {
        const productSets = getProductSets();
        if (!productSets.length) {
            return;
        }
        setOpenSet((prev) => {
            const next = { ...prev };
            let changed = false;
            productSets.forEach((set) => {
                const key = set.ORDERS_PRODUCTS_ID || '';
                if (key && next[key] === undefined) {
                    next[key] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [props.data, props.productsForSets]);

    useEffect(() => {
        if (props.data) {
            let newAllColumns = productsDefaultColumns;
            if (props.newOrder) {
                newAllColumns = newAllColumns.filter((item) => !['MISSING_COUNT', 'CONFIRMED'].includes(item.key));
            }

            setAllColumns(newAllColumns);

            const productSets = getProductSets();
            setData([
                ...props.data
                    .filter((item) => {
                        if (!item.ID_SET) {
                            return true;
                        }
                        if (!props.newOrder) {
                            return false;
                        }
                        // New order: set children render nested under their set row only.
                        return !props.productsForSets?.some((set) => set.ID_SET === item.ID_SET);
                    })
                    .map((item) => flattenOrderProductRow(item)),
                ...productSets,
            ]);
        }
    }, [props.data, props.productsForSets, props.newOrder]);

    const getProductSets = (): OrderProducSetType[] => {
        const { mapProductsInSet } = getProductSetData(props.data || [], props.productsForSets);
        return props.productsForSets?.map((productsForSet) => {
            const products = (mapProductsInSet[String(productsForSet.ID_SET)] || []).map((p) =>
                flattenOrderProductRow(p)
            );
            const unitPrice = productsForSet.ORIGINAL_PRODUCT_PRICE;
            const setCount = productsForSet.COUNT;
            return {
                NAME: productsForSet.NAME,
                SKU: productsForSet.SKU,
                COUNT: productsForSet.COUNT,
                ID_SET: productsForSet.ID_SET,
                MISSING_COUNT: undefined,
                CONFIRMED: productsForSet.CONFIRMED || '',
                COUNTProducts: sumArray(products, 'COUNT'),
                MISSING_COUNTProducts: sumArray(products, 'MISSING_COUNT'),
                CONFIRMEDProducts: sumArray(products, 'CONFIRMED'),
                PRODUCT_PRICE: unitPrice,
                TOTAL_PRICE: computeOrderProductTotalPrice(unitPrice, setCount),
                LINE_STATE: products.find((product) => product.LINE_STATE !== 'D') ? 'E' : 'D',
                ORDERS_PRODUCTS_ID: `set#${productsForSet?.ID}`,
                PRODUCT_TYPE: 'productSet',
                PRODUCT_ID: productsForSet.PRODUCT_ID ?? productsForSet.ID_PRODUCT,
                EAN: productsForSet.EAN,
                WEIGHT: productsForSet.WEIGHT,
                HS_CODE: productsForSet.HS_CODE,
                AGECHECK: productsForSet.AGECHECK,
                DESCRIPTION: productsForSet.DESCRIPTION,
                EXP_METHOD: productsForSet.EXP_METHOD,
                INSERT_TYPE: productsForSet.INSERT_TYPE,
                ROP: productsForSet.ROP,
                EXPIRATION_DAYS: productsForSet.EXPIRATION_DAYS,
                SCAN_SERIAL_NUMBER: productsForSet.SCAN_SERIAL_NUMBER,
                SN_REGEX: productsForSet.SN_REGEX,
                WIDTH: productsForSet.WIDTH,
                HEIGHT: productsForSet.HEIGHT,
                LENGTH: productsForSet.LENGTH,
                PRICE: productsForSet.PRICE,
                UNIT_PRICE: productsForSet.UNIT_PRICE,
                BATTERY_TYPE: productsForSet.BATTERY_TYPE,
                CUSTOM_FIELD_1: productsForSet.CUSTOM_FIELD_1,
                CUSTOM_FIELD_2: productsForSet.CUSTOM_FIELD_2,
                CUSTOM_FIELD_3: productsForSet.CUSTOM_FIELD_3,
                ACTIVE: productsForSet.ACTIVE,
                CREATED_AT: productsForSet.CREATED_AT,
                UPDATED_AT: productsForSet.UPDATED_AT,
                products,
            };
        });
    };

    const handleToggleRow = (row: TableRowType) => {
        const newLineState = row.LINE_STATE === 'D' ? 'E' : 'D';

        if (newLineState === 'E' && isReactivationBlocked(row)) {
            return;
        }

        if (row.PRODUCT_TYPE === 'productSet' && row.ID_SET) {
            const idSet = Number(row.ID_SET);
            if (newLineState === 'D') {
                const setEntry = props.productsForSets.find((set) => set.ID_SET === idSet);
                const { products, productsForSets } = deactivateOrderSet(
                    props.data || [],
                    props.productsForSets,
                    idSet,
                    Number(setEntry?.COUNT || 0)
                );
                props.onChange(products, false);
                props.onChangeProductsForSets(productsForSets);
            } else {
                props.onChange(
                    props.data?.map((item) => {
                        if (Number(item.ID_SET) === idSet) {
                            return { ...item, LINE_STATE: 'E' };
                        }
                        return item;
                    }) || [],
                    false
                );
                props.onChangeProductsForSets(
                    props.productsForSets.map((set) => {
                        if (set.ID_SET === idSet) {
                            return { ...set, LINE_STATE: 'E' };
                        }
                        return set;
                    })
                );
            }
        } else if (Number(row.ORDERS_PRODUCTS_ID) < 0) {
            if (row.LINE_STATE === 'D') {
                props.onChange(
                    props.data?.map((item) => {
                        if (String(item.ORDERS_PRODUCTS_ID) === String(row.ORDERS_PRODUCTS_ID)) {
                            return { ...item, LINE_STATE: 'E' };
                        }
                        return item;
                    }) || [],
                    false
                );
            } else {
                props.onChange(
                    props.data?.filter((item) => item.ORDERS_PRODUCTS_ID != row.ORDERS_PRODUCTS_ID) || [],
                    false
                );
            }
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (String(item.ORDERS_PRODUCTS_ID) === String(row.ORDERS_PRODUCTS_ID)) {
                        return { ...item, LINE_STATE: newLineState };
                    }
                    return item;
                }) || [],
                false
            );
        }
    };

    const toggleOpenSet = (row: TableRowType) => {
        setOpenSet({ ...openSet, [row.ORDERS_PRODUCTS_ID]: !openSet[row.ORDERS_PRODUCTS_ID] });
    };

    const filtersActive = hasActiveFilters(activeFilters);

    const customFilter: FilteringRowType = (row, filters, columns) =>
        row.products
            ? isSetRowVisibleForFilters(row, filters, columns)
            : filteringRow(row, filters, columns);

    const handleChangeOptions = (opts: TablePropsOptionsType) => {
        setActiveFilters(opts.filters || []);
    };

    const renderSetRow: RenderSetRowType = (params) => {
        const {
            row,
            rowIdKey,
            rowSelected,
            rowPopupOpen,
            setSelectedRows,
            visilbeColumns,
            handleSelectRow,
            customRender: rowCustomRender,
            selectOptions,
            rowActions,
            handlePopupOpen,
            onClick,
            last
        } = params;

        const isSetRow = !!row.products;
        let rowSx: SxProps<Theme> = {
            position: 'relative',
            ...(row.LINE_STATE === 'D'
                ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                : {})
        };
        if (isSetRow) {
            rowSx = {
                cursor: 'pointer',
                ...(row.LINE_STATE === 'D'
                    ? {
                        color: colors.text_T3,
                        '& .MuiTableCell-root': { color: colors.text_T3 }
                    }
                    : {})
            };
        }

        let nameElements: ReactNode = null;
        if (!isSetRow) {
            nameElements = (
                <>
                    <SetRowStartVertical last={last} />
                    <SetRowStartHorizontal />
                </>
            );
        } else {
            const isOpen = filtersActive ? true : openSet[row.ORDERS_PRODUCTS_ID];
            nameElements = (
                <span data-testid="order-detail-product-set-toggle">
                    {isOpen ? (
                        <ArrowDropUpIcon sx={arrowStyle} />
                    ) : (
                        <ArrowDropDownIcon sx={arrowStyle} />
                    )}
                </span>
            );
        }

        return (
            <MuiTableRow
                key={String(row[rowIdKey])}
                sx={rowSx}
                className={(rowSelected ? ' table-row-selected' : '') + (rowPopupOpen ? ' table-row-open-popup' : '')}
                onClick={(e) => onClick?.(e)}
            >
                {setSelectedRows && (
                    <MuiTableCell className="row-select">
                        <Checkbox
                            value={!!rowSelected}
                            onChange={(value: number | boolean) => handleSelectRow(row, value)}
                        />
                    </MuiTableCell>
                )}
                {visilbeColumns.map((column) => {
                    const { text, element } = getRowElementText(row, column, rowCustomRender, selectOptions, {
                        formatDisplay: true,
                    });
                    return (
                        <MuiTableCell
                            key={column.key}
                            title={text}
                            style={{
                                ...column.customStyle,
                                ...(isSetRow && column.key === 'PRODUCT_TYPE' ? { fontWeight: 'bold' } : {})
                            }}
                        >
                            {column.key === 'NAME' && isSetRow ? (
                                <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                                    data-testid="order-detail-product-set-name"
                                >
                                    {nameElements}
                                    {element || text}
                                </Box>
                            ) : (
                                <>
                                    {column.key === 'NAME' && nameElements}
                                    {element || text}
                                </>
                            )}
                        </MuiTableCell>
                    );
                })}
                {rowActions ? (
                    <MuiTableCell className="row-action">
                        <TableRowActions
                            rowActions={rowActions}
                            row={row}
                            handlePopupOpen={(value) => handlePopupOpen(value, row)}
                        />
                    </MuiTableCell>
                ) : null}
            </MuiTableRow>
        );
    };

    const customRowRender: CustomRowRenderType = (params) => {
        const { row, rowIdKey } = params;
        if (!row.products) {
            return undefined;
        }
        const visibleProducts = getVisibleSetProducts(row.products, activeFilters, allColumns);
        const isOpen = filtersActive ? true : openSet[row.ORDERS_PRODUCTS_ID];
        return (
            <React.Fragment key={String(row[rowIdKey])}>
                {renderSetRow({
                    ...params,
                    onClick: (e) => {
                        const target = e.target as HTMLElement;
                        if (
                            !target.closest('.row-action') &&
                            !target.closest('.MuiInputBase-root')
                        ) {
                            toggleOpenSet(row);
                        }
                    }
                })}
                {isOpen &&
                    visibleProducts.map((productRow: OrderProductType, index: number) =>
                        renderSetRow({ ...params, row: productRow, last: index + 1 === visibleProducts.length })
                    )}
            </React.Fragment>
        );
    };

    const sumRows = data.reduce<TableRowType[]>((acc, row) => {
        if (row.products?.length) {
            const products = row.products.filter((product) => product.LINE_STATE !== 'D');
            acc.push({
                ...row,
                COUNT: sumArray(products, 'COUNT'),
                COUNT_SET: row.COUNT,
                MISSING_COUNT: sumArray(products, 'MISSING_COUNT'),
                CONFIRMED: sumArray(products, 'CONFIRMED'),
                TOTAL_PRICE: computeOrderProductTotalPrice(row.PRODUCT_PRICE, row.COUNT),
            });
        } else {
            acc.push(row);
        }
        return acc;
    }, []);

    return (
        <TableSimple
            rowIdKey="ORDERS_PRODUCTS_ID"
            rows={data || []}
            allFilteredRows={sumRows}
            columsLabelPrefix="orderDetail.productTable"
            allColumns={allColumns}
            initSort={{ column: 'ORDERS_PRODUCTS_ID', direction: 'ASC', createIdNumber: true }}
            dataTestId="order-detail-products-table"
            userSettingsName="showOrderDetailProductsTableColumns"
            userSettingsData={userSettingsData}
            editUserSettings={editUserSettings}
            rowActions={[
                {
                    icon: <HighlightOffIcon />,
                    label: translate('orderDetail.productTable.action.removeProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'D' || !!props.summary,
                    style: { height: sizes.table_action_with_input_H },
                },
                {
                    icon: <ProductCustomizationIcon />,
                    label: translate('orderDetail.productTable.action.orderProductDetail'),
                    onClick: (row) => props.setOpenProductDetailId(row.ORDERS_PRODUCTS_ID),
                    hiddenFce: (row) => !!row.products || !props.allowOrderProductDetail,
                    style: { height: sizes.table_action_with_input_H },
                },
                {
                    icon: <AddCircleIcon />,
                    label: translate('orderDetail.productTable.action.addProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) =>
                        !!props.disableEdit ||
                        row.LINE_STATE === 'E' ||
                        !!props.summary ||
                        isReactivationBlocked(row),
                    style: { height: sizes.table_action_with_input_H },
                },
            ]}
            selectOptions={{
                productTypeCodes: extendProductTypeCodes,
                orderProductCustomizationType: props.orderProductCustomizationTypeCodes,
                expeditionMethodCodes,
                productInsetTypeCodes,
                boolNumberCodes,
                scanImeiSnCodes,
                batteryTypeCodes,
                currencyCodes,
            }}
            optionStyleFce={colorOptionFce}
            rowSxFce={(row) =>
                row.LINE_STATE === 'D'
                    ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                    : {}
            }
            sumCols={{
                COUNT: true,
                MISSING_COUNT: (rows) => {
                    const count = sumArray(rows, 'MISSING_COUNT');
                    if (count > 0) {
                        return <span style={{ color: colors.alert_O }}>{formatNumber(count)}</span>;
                    }
                    return formatNumber(count);
                },
                CONFIRMED: true,
                TOTAL_PRICE: (rows) => {
                    return formatNumber(
                        sumArraySimple(
                            rows.map((row: TableRowType) =>
                                Number(
                                    row.TOTAL_PRICE ??
                                        Number(row.PRODUCT_PRICE) * Number(row.COUNT_SET || row.COUNT)
                                )
                            )
                        )
                    );
                }
            }}
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
            maxHeight={'unset'}
            customRowRender={customRowRender}
            customRender={customRender}
            customFilter={customFilter}
            onChangeOptions={handleChangeOptions}
        />
    );
};

export default ProductsTable;
